// src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import DashboardTasks from "../components/DashboardTasks";
import Analytics from "../components/Analytics";
import { DashboardProvider } from "../contexts/DashboardContext";
import DashboardReminders from "../components/DashboardReminders";
import WorkHoursTracker from "../components/WorkHourTracker";
import SpendingsAndIncomeTracker from "../components/SpendingsAndIncomeTracker";
import { FinanceProvider } from "../contexts/FinanceContext";
import HabitTracker from "../components/HabitTracker";
import Streaks from "../components/Streaks";
import { useAuth } from "../contexts/AuthContext";
import { getAuth } from "firebase/auth";
import HomeGoalTracker from "../components/HomeGoalTracker";
import HomeGoalTrackerPreview from "../components/HomeGoalTrackerPreview";
import { RemindersProvider } from "../contexts/RemindersContext";
import { FEATURES, canAccessFeature } from "../utils/entitlements";
import Navbar from "../components/Navbar";
import StreaksPreview from "../components/StreaksPreview";
import GrowthStats from "../components/GrowthStats";
import GrowthStatsPreview from "../components/GrowthStatsPreview";

// --- STYLED COMPONENTS ---
const Container = styled.div`
  padding: 0;
  min-height: 100vh;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  font-family: "Montserrat", sans-serif;
  background: var(--primaryBackgroundColor);
  transition: all 0.3s ease;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: 1400px) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    gap: 1rem;
    padding: 0 0.5rem;
  }
  @media (max-width: 650px) {
    margin-top: 80px;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    padding: 0 0.25rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  width: 100%;
  padding: 0 0rem;
  margin-top: 1rem;
  box-sizing: border-box;

  /* Default for > 1850px */
  grid-template-columns: repeat(3, 1fr);
  grid-template-areas:
    "tasks habits goals"
    "finance workhours streaks"
    "growth growth reminders";

  /* 1400px - 1850px - Finance and Work Hours equal (50-50), 3 items in third row */
  @media (max-width: 1850px) and (min-width: 1401px) {
    grid-template-columns: repeat(6, 1fr); /* 6 columns gives us flexibility */
    grid-template-areas:
      "tasks tasks habits habits goals goals"
      "finance finance finance workhours workhours workhours"
      "streaks streaks growth growth reminders reminders";
  }

  /* 700px - 1400px */
  @media (max-width: 1400px) and (min-width: 701px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      "tasks habits"
      "goals finance"
      "workhours streaks"
      "growth reminders";
  }

  /* Mobile: < 700px */
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "tasks"
      "habits"
      "goals"
      "finance"
      "workhours"
      "streaks"
      "growth"
      "reminders";
    /* padding: 0 1rem; */
    gap: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0 0.5rem;
    gap: 0.75rem;
  }
`;

// Grid item with grid-area assignment
const GridItem = styled.div`
  grid-area: ${(props) => props.$area};
  min-height: ${(props) => props.$minHeight || "300px"};
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Specific heights for different sections */
  &[data-area="tasks"],
  &[data-area="habits"],
  &[data-area="goals"] {
    min-height: 400px;
  }

  &[data-area="finance"] {
    min-height: 350px;
  }

  &[data-area="growth"] {
    min-height: 350px;
  }

  /* Mobile adjustments */
  @media (max-width: 700px) {
    min-height: ${(props) => props.$mobileMinHeight || "300px"} !important;
    min-width: 100% !important;
  }

  @media (max-width: 480px) {
    min-height: ${(props) => props.$mobileMinHeight || "250px"} !important;
    border-radius: 10px;
  }
`;

// Special wrapper for wide components
const WideGridItem = styled(GridItem)`
  /* Finance takes 2 columns on > 1850px */
  &[data-area="finance"][data-width="wide"] {
    @media (min-width: 1851px) {
      grid-column: span 2;
    }

    @media (max-width: 1850px) and (min-width: 1401px) {
      grid-column: span 2;
    }
  }

  /* Analytics takes 2 columns on > 1850px */
  &[data-area="growth"][data-width="wide"] {
    @media (min-width: 1851px) {
      grid-column: span 2;
    }
  }
`;

const UpgradePrompt = styled.div`
  width: 100%;
  height: 100%;
  padding: 2rem;
  background: var(--secondaryBackgroundColor);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--fontSecondaryColor);
  border: 2px dashed var(--shadowColor);
  transition: all 0.3s ease;
  min-height: 300px;

  &:hover {
    border-color: var(--primaryColor);
    transform: translateY(-2px);
  }

  h4 {
    margin: 0 0 1rem 0;
    color: var(--fontPrimaryColor);
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    margin: 0 0 1.5rem 0;
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 300px;
  }

  a {
    color: var(--primaryColor);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.95rem;
    padding: 0.75rem 1.5rem;
    border: 1px solid var(--primaryColor);
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: var(--primaryColor);
      color: white;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 250px;

    h4 {
      font-size: 1.1rem;
    }

    p {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 200px;

    h4 {
      font-size: 1rem;
      margin-bottom: 0.75rem;
    }

    p {
      font-size: 0.85rem;
      margin-bottom: 1rem;
    }

    a {
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
    }
  }
`;
// --- END STYLED COMPONENTS ---

const HabitProvider = ({ children }) => <>{children}</>;

// Helper component to show a locked feature
const LockedFeature = ({ featureName, requiredTier }) => (
  <UpgradePrompt>
    <h4>{featureName}</h4>
    <p>
      Requires the <strong>{requiredTier}</strong> package or higher.
    </p>
    <a href="/upgrade">Upgrade Now</a>
  </UpgradePrompt>
);

export default function Home({ theme, switchTheme }) {
  const { subscriptionTier } = useAuth();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Home - ASites Planner";
  }, []);

  // Helper function to render a component or a locked state
  const renderFeature = (featureKey, Component, requiredTier, props = {}) => {
    if (canAccessFeature(subscriptionTier, featureKey)) {
      return <Component {...props} />;
    }
    return (
      <LockedFeature
        featureName={featureKey.replace("_", " ")}
        requiredTier={requiredTier}
      />
    );
  };

  // Special function for Goal Tracker that shows preview for free users
  const renderGoalTracker = () => {
    const hasAccess = canAccessFeature(subscriptionTier, FEATURES.GOAL_TRACKER);

    if (hasAccess) {
      return <HomeGoalTracker />;
    } else {
      return <HomeGoalTrackerPreview />;
    }
  };

  return (
    <DashboardProvider>
      <RemindersProvider>
        <HabitProvider>
          <Container>
            <Navbar theme={theme} switchTheme={switchTheme} />

            <DashboardGrid>
              {/* Row 1: Tasks, Habits, Goals */}
              <GridItem
                $area="tasks"
                data-area="tasks"
                $mobileMinHeight="400px"
              >
                {renderFeature(
                  FEATURES.DASHBOARD_TASKS,
                  DashboardTasks,
                  "FREE"
                )}
              </GridItem>

              <GridItem
                $area="habits"
                data-area="habits"
                $mobileMinHeight="400px"
              >
                {renderFeature(FEATURES.HABIT_TRACKER, HabitTracker, "FREE")}
              </GridItem>

              <GridItem
                $area="goals"
                data-area="goals"
                $mobileMinHeight="400px"
              >
                {renderGoalTracker()}
              </GridItem>

              {/* Row 2: Finance, Work Hours, Streaks (or Finance, Work Hours for < 1850px) */}
              <WideGridItem
                $area="finance"
                data-area="finance"
                // data-width="wide"
                $mobileMinHeight="350px"
              >
                {canAccessFeature(
                  subscriptionTier,
                  FEATURES.FINANCE_TRACKER
                ) ? (
                  <FinanceProvider>
                    <SpendingsAndIncomeTracker theme={theme} />
                  </FinanceProvider>
                ) : (
                  <LockedFeature
                    featureName="Finance Tracker"
                    requiredTier="PRO"
                  />
                )}
              </WideGridItem>

              <GridItem
                $area="workhours"
                data-area="workhours"
                $mobileMinHeight="300px"
              >
                {renderFeature(FEATURES.WORK_HOURS, WorkHoursTracker, "PRO", {
                  theme,
                })}
              </GridItem>

              <GridItem
                $area="streaks"
                data-area="streaks"
                $mobileMinHeight="300px"
              >
                {canAccessFeature(subscriptionTier, FEATURES.STREAKS) ? (
                  <Streaks /> // Real component for PRO users
                ) : (
                  <StreaksPreview /> // Preview for FREE users
                )}
              </GridItem>

              {/* Row 3: Analytics and Reminders */}
              <WideGridItem
                $area="growth"
                data-area="growth"
                data-width="wide"
                $mobileMinHeight="350px"
              >
                {canAccessFeature(subscriptionTier, FEATURES.GROWTH) ? (
                  <GrowthStats />
                ) : (
                  <GrowthStatsPreview />
                )}
              </WideGridItem>

              <GridItem
                $area="reminders"
                data-area="reminders"
                $mobileMinHeight="300px"
              >
                {renderFeature(
                  FEATURES.DASHBOARD_REMINDERS,
                  DashboardReminders,
                  "GOLD"
                )}
              </GridItem>
            </DashboardGrid>
          </Container>
        </HabitProvider>
      </RemindersProvider>
    </DashboardProvider>
  );
}
